import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle2, 
  MapPin, 
  Building2, 
  Home, 
  DollarSign, 
  HelpCircle, 
  User,
  Loader2,
  AlertTriangle
} from "lucide-react";
import axios from 'axios';
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STEPS = [
  { id: 'location', title: 'Ubicación', icon: MapPin },
  { id: 'basics', title: 'Datos básicos', icon: Building2 },
  { id: 'details', title: 'Inmueble', icon: Home },
  { id: 'building', title: 'Edificio', icon: Building2 },
  { id: 'price', title: 'Precio', icon: DollarSign },
  { id: 'reason', title: 'Motivo', icon: HelpCircle },
  { id: 'personal', title: 'Personal', icon: User },
];

export function SellDirectForm() {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<any>(null);

  const [formData, setFormData] = useState({
    // Step 1
    address: '',
    commune: '',
    // Step 2
    unitNumber: '',
    floor: '',
    tower: 'Única',
    interiorArea: '',
    exteriorArea: '0',
    // Step 3
    bedrooms: '',
    bathrooms: '',
    parkings: '0',
    storages: '0',
    bikeRacks: '0',
    occupancyStatus: '',
    // Step 4
    yearBuilt: '',
    rol: '',
    // Step 5
    currency: 'UF',
    askedPrice: '',
    // Step 6
    saleReason: '',
    // Step 7
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isBroker: false,
  });

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/direct-offers', formData);
      setResult(response.data);
      setSubmitted(true);
      toast.success("Solicitud enviada correctamente");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Hubo un error al procesar tu solicitud");
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-2">
              <h3 className="text-2xl font-display font-black uppercase italic">¿Dónde está ubicada tu propiedad?</h3>
              <p className="text-muted-foreground font-serif italic">No te tomará más de cinco minutos</p>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address">Dirección completa</Label>
                <Input 
                  id="address" 
                  placeholder="Ej: Av. Vitacura 1234, Depto 502" 
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="commune">Comuna</Label>
                <Select value={formData.commune} onValueChange={(v) => handleChange('commune', v)}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Seleccionar comuna" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Las Condes">Las Condes</SelectItem>
                    <SelectItem value="Vitacura">Vitacura</SelectItem>
                    <SelectItem value="Lo Barnechea">Lo Barnechea</SelectItem>
                    <SelectItem value="Providencia">Providencia</SelectItem>
                    <SelectItem value="Ñuñoa">Ñuñoa</SelectItem>
                    <SelectItem value="Santiago">Santiago</SelectItem>
                    <SelectItem value="La Reina">La Reina</SelectItem>
                    <SelectItem value="Colina">Colina</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-2">
              <h3 className="text-2xl font-display font-black uppercase italic">Departamento, piso y metros cuadrados</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="unitNumber">N° de departamento</Label>
                <Input 
                  id="unitNumber" 
                  placeholder="Ej: 1201, A, B12" 
                  value={formData.unitNumber}
                  onChange={(e) => handleChange('unitNumber', e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="floor">Piso</Label>
                <Input 
                  id="floor" 
                  placeholder="Ej: 12" 
                  value={formData.floor}
                  onChange={(e) => handleChange('floor', e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tower">Torre (opcional)</Label>
                <Input 
                  id="tower" 
                  placeholder="Ej: Única" 
                  value={formData.tower}
                  onChange={(e) => handleChange('tower', e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="interiorArea">Área interior (m²)</Label>
                <Input 
                  id="interiorArea" 
                  type="number"
                  placeholder="Ej: 106" 
                  value={formData.interiorArea}
                  onChange={(e) => handleChange('interiorArea', e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exteriorArea">Área exterior / Terraza (m²)</Label>
                <Input 
                  id="exteriorArea" 
                  type="number"
                  placeholder="Ej: 12" 
                  value={formData.exteriorArea}
                  onChange={(e) => handleChange('exteriorArea', e.target.value)}
                  className="h-12"
                />
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-2">
              <h3 className="text-2xl font-display font-black uppercase italic">Dormitorios, baños y características</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Dormitorios</Label>
                <Select value={formData.bedrooms} onValueChange={(v) => handleChange('bedrooms', v)}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Baños</Label>
                <Select value={formData.bathrooms} onValueChange={(v) => handleChange('bathrooms', v)}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="parkings">Estacionamientos</Label>
                <Input 
                  id="parkings" 
                  type="number"
                  value={formData.parkings}
                  onChange={(e) => handleChange('parkings', e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="storages">Bodegas</Label>
                <Input 
                  id="storages" 
                  type="number"
                  value={formData.storages}
                  onChange={(e) => handleChange('storages', e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="occupancyStatus">Estado de ocupación</Label>
                <Select value={formData.occupancyStatus} onValueChange={(v) => handleChange('occupancyStatus', v)}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="empty">Vacío</SelectItem>
                    <SelectItem value="occupied_owner">Ocupado por propietario</SelectItem>
                    <SelectItem value="occupied_tenant">Ocupado por arrendatario</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-2">
              <h3 className="text-2xl font-display font-black uppercase italic">Detalles del edificio</h3>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="yearBuilt">Año de construcción</Label>
                <Input 
                  id="yearBuilt" 
                  type="number"
                  placeholder="Ej: 2015" 
                  value={formData.yearBuilt}
                  onChange={(e) => handleChange('yearBuilt', e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rol">ROL de la propiedad</Label>
                <Input 
                  id="rol" 
                  placeholder="Ej: 1006-4" 
                  value={formData.rol}
                  onChange={(e) => handleChange('rol', e.target.value)}
                  className="h-12"
                />
                <p className="text-[10px] font-serif italic text-muted-foreground">El ROL es un identificador único de la propiedad en el catastro</p>
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-2">
              <h3 className="text-2xl font-display font-black uppercase italic">¿A qué precio quieres vender?</h3>
            </div>
            <div className="space-y-6">
              <div className="space-y-3">
                <Label>Moneda</Label>
                <RadioGroup 
                  value={formData.currency} 
                  onValueChange={(v) => handleChange('currency', v)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="UF" id="uf" />
                    <Label htmlFor="uf">UF (Unidad de Fomento)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="CLP" id="clp" />
                    <Label htmlFor="clp">CLP (Pesos Chilenos)</Label>
                  </div>
                </RadioGroup>
              </div>
              <div className="space-y-2">
                <Label htmlFor="askedPrice">Precio de venta ({formData.currency})</Label>
                <Input 
                  id="askedPrice" 
                  type="number"
                  placeholder={formData.currency === 'UF' ? "Ej: 2.500" : "Ej: 120.000.000"} 
                  value={formData.askedPrice}
                  onChange={(e) => handleChange('askedPrice', e.target.value)}
                  className="h-12"
                />
                <div className="p-4 bg-parchment border brutalist-border-sm mt-4">
                  <p className="text-xs font-black uppercase tracking-widest mb-2">Rangos recomendados:</p>
                  <ul className="text-xs font-serif italic space-y-1 opacity-60">
                    <li>• UF: Entre 1.000 y 4.500</li>
                    <li>• CLP: Entre $40M y $180M</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-2">
              <h3 className="text-2xl font-display font-black uppercase italic">¿Por qué quieres vender tu propiedad?</h3>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {[
                "Solo quiero conocer la evaluación",
                "Quiero comprar otra propiedad",
                "Necesito dinero para pagar deudas",
                "Quiero invertir en un negocio",
                "Todavía no sé qué hacer con el dinero",
                "Otro"
              ].map((reason) => (
                <div 
                  key={reason}
                  onClick={() => handleChange('saleReason', reason)}
                  className={cn(
                    "p-4 brutalist-border-sm cursor-pointer transition-all font-serif italic",
                    formData.saleReason === reason ? "bg-ink text-parchment" : "bg-white hover:bg-parchment"
                  )}
                >
                  {reason}
                </div>
              ))}
            </div>
          </div>
        );
      case 6:
        return (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-2">
              <h3 className="text-2xl font-display font-black uppercase italic">Tus datos personales</h3>
              <p className="text-xs font-serif italic opacity-60">Tu información personal no se divulgará.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Nombre</Label>
                <Input 
                  id="firstName" 
                  placeholder="Tu nombre" 
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Apellido</Label>
                <Input 
                  id="lastName" 
                  placeholder="Tu apellido" 
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="email">Correo</Label>
                <Input 
                  id="email" 
                  type="email"
                  placeholder="ejemplo@mail.com" 
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Input 
                  id="phone" 
                  placeholder="+56912345678" 
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="flex items-center space-x-2 pt-4">
                <Checkbox 
                  id="isBroker" 
                  checked={formData.isBroker}
                  onCheckedChange={(v) => handleChange('isBroker', !!v)}
                />
                <Label htmlFor="isBroker">¿Eres corredor de propiedades?</Label>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (submitted && result) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4 animate-in fade-in zoom-in duration-500">
        <Card className="p-12 brutalist-border bg-ink text-parchment relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 grain-texture pointer-events-none" />
          <div className="relative z-10 text-center space-y-8">
            <CheckCircle2 className="h-20 w-20 text-terracotta mx-auto" />
            
            <div className="space-y-2">
              <h2 className="text-4xl font-display font-black uppercase italic">¡Solicitud Procesada!</h2>
              <p className="font-serif italic text-parchment/60">Hemos analizado tu propiedad mediante nuestro algoritmo.</p>
            </div>

            {result.isFeasible ? (
              <div className="bg-parchment/10 p-8 brutalist-border-sm border-terracotta space-y-6">
                <div className="space-y-1">
                  <span className="text-[10px] font-black uppercase tracking-widest text-terracotta">Oferta Preliminar al Contado</span>
                  <div className="text-5xl font-display font-black italic">
                    {result.offer.amount_uf.toLocaleString('es-CL')} UF
                  </div>
                  <p className="text-sm font-serif italic text-parchment/40">
                    Equivalente aprox a ${result.offer.amount_clp.toLocaleString('es-CL')} CLP
                  </p>
                </div>
                
                <div className="pt-6 border-t border-parchment/10 text-left space-y-4">
                  <p className="text-sm font-serif italic">Esta oferta es válida por 7 días y está sujeta a una inspección técnica presencial.</p>
                  <div className="flex items-center gap-2 text-moss">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="text-[10px] font-black uppercase tracking-widest">Factibilidad Aprobada</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-parchment/10 p-8 brutalist-border-sm border-amber-500 space-y-6">
                <div className="flex items-center justify-center gap-3 text-amber-500">
                  <AlertTriangle className="h-8 w-8" />
                  <h3 className="text-xl font-display font-black uppercase italic">Evaluación Técnica Pendiente</h3>
                </div>
                <p className="font-serif italic text-sm text-parchment/60 leading-relaxed">
                  Tu propiedad requiere un análisis más detallado por parte de nuestro equipo de adquisiciones. Te contactaremos en menos de 48 horas hábiles.
                </p>
              </div>
            )}

            <div className="pt-8">
              <Button 
                variant="terracotta" 
                className="w-full h-16 text-lg"
                onClick={() => window.location.href = '/'}
              >
                VOLVER AL INICIO
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-12 px-4">
      {/* Progress Bar */}
      <div className="mb-12">
        <div className="flex justify-between mb-4">
          {STEPS.map((step, i) => (
            <div 
              key={step.id} 
              className={cn(
                "flex flex-col items-center gap-2 transition-all duration-300",
                i <= currentStep ? "text-ink" : "text-muted-foreground opacity-30"
              )}
            >
              <div className={cn(
                "h-8 w-8 rounded-full flex items-center justify-center border-2",
                i < currentStep ? "bg-ink border-ink text-parchment" : 
                i === currentStep ? "border-terracotta text-terracotta" : "border-muted-foreground"
              )}>
                {i < currentStep ? <CheckCircle2 className="h-4 w-4" /> : <step.icon className="h-4 w-4" />}
              </div>
              <span className="text-[8px] font-black uppercase tracking-widest hidden md:block">{step.title}</span>
            </div>
          ))}
        </div>
        <div className="h-1 bg-muted-foreground/10 w-full overflow-hidden">
          <div 
            className="h-full bg-terracotta transition-all duration-500 ease-out"
            style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
          />
        </div>
      </div>

      <Card className="p-8 md:p-12 brutalist-border bg-white relative overflow-hidden">
        {renderStep()}

        <div className="mt-12 flex justify-between pt-8 border-t border-ink/5">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0 || loading}
            className="h-12 px-8"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> ANTERIOR
          </Button>

          {currentStep === STEPS.length - 1 ? (
            <Button
              variant="terracotta"
              onClick={handleSubmit}
              disabled={loading}
              className="h-12 px-8"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              OBTENER OFERTA <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="default"
              onClick={nextStep}
              className="h-12 px-8 bg-ink text-parchment hover:bg-terracotta"
            >
              SIGUIENTE <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </Card>

      <p className="text-center mt-8 text-[10px] font-serif italic text-muted-foreground">
        Al continuar, aceptas nuestros términos de servicio y política de privacidad.
      </p>
    </div>
  );
}
